/**
 * Guest History Modal Component
 * Displays detailed change history for a specific guest
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, 
  Calendar, 
  User, 
  Clock, 
  CheckCircle, 
  XCircle,
  Users,
  Send,
  Phone
} from 'lucide-react';
import { analyticsService, GuestEventHistoryEntry } from '../../services/analyticsService';
import { useAppSelector } from '../../store/hooks';

interface GuestHistoryModalProps {
  guestId: string;
  guestName: string;
  isOpen: boolean;
  onClose: () => void;
}

type ChangeTypeFilter = 'all' | 'status_change' | 'companion_update' | 'attendance_update' | 'response_update';

const getChangeTypeColors = (theme: 'light' | 'dark') => {
  if (theme === 'dark') {
    return {
      status_change: 'bg-blue-900/30 text-blue-300 border-blue-700/50',
      companion_update: 'bg-purple-900/30 text-purple-300 border-purple-700/50',
      attendance_update: 'bg-green-900/30 text-green-300 border-green-700/50',
      response_update: 'bg-yellow-900/30 text-yellow-300 border-yellow-700/50',
      field_update: 'bg-gray-800/50 text-gray-300 border-gray-700/50',
      invitation_sent: 'bg-indigo-900/30 text-indigo-300 border-indigo-700/50',
      whatsapp_update: 'bg-emerald-900/30 text-emerald-300 border-emerald-700/50',
    };
  } else {
    return {
      status_change: 'bg-blue-100 text-blue-800 border-blue-300',
      companion_update: 'bg-purple-100 text-purple-800 border-purple-300',
      attendance_update: 'bg-green-100 text-green-800 border-green-300',
      response_update: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      field_update: 'bg-gray-100 text-gray-800 border-gray-300',
      invitation_sent: 'bg-indigo-100 text-indigo-800 border-indigo-300',
      whatsapp_update: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    };
  }
};

const getChangeIcon = (changeType: string) => {
  switch (changeType) {
    case 'status_change':
      return <CheckCircle className="w-4 h-4" />;
    case 'attendance_update':
      return <Users className="w-4 h-4" />;
    case 'response_update':
      return <Send className="w-4 h-4" />;
    case 'invitation_sent':
      return <Send className="w-4 h-4" />;
    case 'whatsapp_update':
      return <Phone className="w-4 h-4" />;
    default:
      return <Clock className="w-4 h-4" />;
  }
};

export const GuestHistoryModal: React.FC<GuestHistoryModalProps> = ({
  guestId,
  guestName,
  isOpen,
  onClose
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [history, setHistory] = useState<GuestEventHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle scroll locking and escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Lock body scroll
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('keydown', handleEscape);
        // Restore body scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, onClose]);
  const [filter, setFilter] = useState<ChangeTypeFilter>('all');
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  
  const changeTypeColors = getChangeTypeColors(theme);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await analyticsService.getGuestHistory(guestId);
      setHistory(response.history);
    } catch (err: any) {
      setError(err.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [guestId]);

  useEffect(() => {
    if (isOpen && guestId) {
      fetchHistory();
    }
  }, [isOpen, guestId, fetchHistory]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatChangeValue = (value: string | null) => {
    if (value === null || value === 'None' || value === '') return 'N/A';
    if (value === 'true') return 'Yes';
    if (value === 'false') return 'No';
    return value;
  };

  // Group history by event
  const historyByEvent = history.reduce((acc, entry) => {
    if (!acc[entry.event_title]) {
      acc[entry.event_title] = [];
    }
    acc[entry.event_title].push(entry);
    return acc;
  }, {} as Record<string, GuestEventHistoryEntry[]>);

  // Filter entries
  const filteredHistoryByEvent = Object.entries(historyByEvent).reduce((acc, [event, entries]) => {
    const filtered = entries.filter(entry => {
      if (filter === 'all') return true;
      return entry.change_type === filter;
    });
    if (filtered.length > 0) {
      acc[event] = filtered;
    }
    return acc;
  }, {} as Record<string, GuestEventHistoryEntry[]>);

  // Sort events by most recent first
  const sortedEvents = Object.entries(filteredHistoryByEvent).sort((a, b) => {
    const aLatest = a[1][0]?.changed_at || '';
    const bLatest = b[1][0]?.changed_at || '';
    return new Date(bLatest).getTime() - new Date(aLatest).getTime();
  });

  const toggleEvent = (eventTitle: string) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventTitle)) {
        newSet.delete(eventTitle);
      } else {
        newSet.add(eventTitle);
      }
      return newSet;
    });
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModalScroll = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  const handleModalTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4"
      onClick={handleOverlayClick}
      onWheel={handleModalScroll}
      onTouchMove={handleModalTouchMove}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />

      <div 
        className="relative w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overscroll-contain"
        onWheel={handleModalScroll}
        onTouchMove={handleModalTouchMove}
      >
        <div 
          className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full flex flex-col`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between border-b flex-shrink-0`}>
            <div>
              <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Change History for {guestName}
              </h3>
              <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {history.length} change{history.length !== 1 ? 's' : ''} recorded
              </p>
            </div>
            <button
              onClick={onClose}
              className={`${theme === 'dark' ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Filters */}
          <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b flex-shrink-0 ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            <div className="flex flex-wrap gap-3">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as ChangeTypeFilter)}
                className={`px-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  theme === 'dark' 
                    ? 'border-gray-600 bg-gray-700 text-white' 
                    : 'border-gray-300 bg-white text-gray-900'
                }`}
              >
                <option value="all">All Changes</option>
                <option value="status_change">Status Changes</option>
                <option value="companion_update">Companion Updates</option>
                <option value="attendance_update">Attendance Updates</option>
                <option value="response_update">Response Updates</option>
              </select>

            </div>
          </div>

          {/* Content */}
          <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1 min-h-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className={`ml-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Loading history...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <p className={theme === 'dark' ? 'text-red-400' : 'text-red-600'}>{error}</p>
              </div>
            ) : sortedEvents.length === 0 ? (
              <div className="text-center py-12">
                <Clock className={`w-12 h-12 mx-auto mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>No history records found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sortedEvents.map(([eventTitle, entries]) => {
                  const isExpanded = expandedEvents.has(eventTitle);
                  const latestEntry = entries[0];
                  return (
                    <div
                      key={eventTitle}
                      className={`border rounded-lg overflow-hidden ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}
                    >
                      {/* Event Header - Clickable */}
                      <button
                        onClick={() => toggleEvent(eventTitle)}
                        className={`w-full px-4 py-3 ${theme === 'dark' ? 'bg-gray-900 hover:bg-gray-800' : 'bg-gray-50 hover:bg-gray-100'} transition-colors flex items-center justify-between text-left`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <Calendar className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                          <div className="flex-1">
                            <div className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {eventTitle}
                            </div>
                            <div className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              {entries.length} change{entries.length !== 1 ? 's' : ''} • Last: {formatDate(latestEntry.changed_at)}
                            </div>
                          </div>
                        </div>
                        <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                          <svg className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>

                      {/* Change Entries - Collapsible */}
                      {isExpanded && (
                        <div className={`${theme === 'dark' ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-100'} divide-y`}>
                          {entries.map((entry) => (
                            <div key={entry.id} className={`px-4 py-3 transition-colors ${theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}>
                              {/* Change Type Badge */}
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-xs font-medium ${changeTypeColors[entry.change_type as keyof typeof changeTypeColors] || changeTypeColors.field_update}`}>
                                  {getChangeIcon(entry.change_type)}
                                  <span className="capitalize">{entry.change_type.replace('_', ' ')}</span>
                                </div>
                                <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {formatDate(entry.changed_at)}
                                </span>
                              </div>

                              {/* Change Details */}
                              <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                                <div>
                                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>From:</span>
                                  <span className={`ml-2 font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>{formatChangeValue(entry.old_value)}</span>
                                </div>
                                <div>
                                  <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>To:</span>
                                  <span className={`ml-2 font-medium ${theme === 'dark' ? 'text-green-400' : 'text-green-700'}`}>{formatChangeValue(entry.new_value)}</span>
                                </div>
                              </div>

                              {/* Changed By */}
                              {entry.changed_by_name && (
                                <div className={`flex items-center gap-1 mt-2 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                  <User className="w-3 h-3" />
                                  <span>{entry.changed_by_name}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={`${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} px-4 sm:px-6 py-4 flex justify-end border-t flex-shrink-0`}>
            <button
              onClick={onClose}
              className={`px-4 py-2 text-white rounded-md transition-colors ${
                theme === 'dark' 
                  ? 'bg-blue-700 hover:bg-blue-600' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestHistoryModal;

